import { StyleSheet } from "react-native";
import { Image } from "expo-image";
import { Globe } from "lucide-react-native";
import { useTheme } from "../../hooks/useTheme";

/**
 * Round-disc market artwork, shared byte-for-byte with the marketing skills
 * page (`apps/web/public/images/flags`). The discs are 267x267 squares that
 * already carry their own rim highlight, so they render 1:1 with no tile,
 * border, or synthetic background behind them.
 *
 * They are loaded as Metro assets rather than inlined SVG strings: `omn.svg`
 * alone is 148KB of emblem detail and the globe is 793KB, which is far too
 * much to carry as JavaScript. `svg` is in Metro's `assetExts`, so a static
 * `require` resolves to a URI on native and web alike.
 */
const FLAG_ASSET_BY_REGION: Record<string, number> = {
  uae: require("../../../assets/flags/uae.svg"),
  ae: require("../../../assets/flags/uae.svg"),
  "united arab emirates": require("../../../assets/flags/uae.svg"),
  ksa: require("../../../assets/flags/ksa.svg"),
  sa: require("../../../assets/flags/ksa.svg"),
  "saudi arabia": require("../../../assets/flags/ksa.svg"),
  kwt: require("../../../assets/flags/kwt.svg"),
  kw: require("../../../assets/flags/kwt.svg"),
  kuwait: require("../../../assets/flags/kwt.svg"),
  qat: require("../../../assets/flags/qat.svg"),
  qa: require("../../../assets/flags/qat.svg"),
  qatar: require("../../../assets/flags/qat.svg"),
  bhr: require("../../../assets/flags/bhr.svg"),
  bh: require("../../../assets/flags/bhr.svg"),
  bahrain: require("../../../assets/flags/bhr.svg"),
  omn: require("../../../assets/flags/omn.svg"),
  om: require("../../../assets/flags/omn.svg"),
  oman: require("../../../assets/flags/omn.svg"),
  // Pan-Gulf rows are not a country, so they fly the globe disc instead.
  gulf: require("../../../assets/flags/globe.svg"),
  gcc: require("../../../assets/flags/globe.svg"),
  "gulf region": require("../../../assets/flags/globe.svg"),
};

/** UAE flag kept as a dedicated export for existing single-market callers. */
export function UaeFlagIcon({ size = 20 }: { size?: number }) {
  return <GulfFlagIcon region="uae" size={size} />;
}

export function GulfFlagIcon({
  region,
  size = 20,
}: {
  region: string;
  size?: number;
}) {
  const { theme } = useTheme();
  const asset = FLAG_ASSET_BY_REGION[region.trim().toLowerCase()];

  if (!asset) {
    return (
      <Globe
        size={Math.round(size * 0.9)}
        color={theme.textTertiary}
        strokeWidth={1.75}
      />
    );
  }

  return (
    <Image
      source={asset}
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        // Qatar and Bahrain are mostly white, so on a light row the disc would
        // otherwise dissolve into the background. A hairline ring keeps every
        // market reading as the same circle.
        borderWidth: StyleSheet.hairlineWidth,
        borderColor: theme.border,
      }}
      contentFit="cover"
      // The discs are flat vector art; a fade reads as a flicker in a list.
      transition={0}
      accessibilityIgnoresInvertColors
    />
  );
}
